// web/src/components/lifecycle/MasterSetupWizard.tsx
//
// First-time master-password setup. A four-step wizard routed at
// `/setup/:step`:
//   Step 1: pick a passphrase (generator by default; "Type my own"
//           switches to a text input + zxcvbn strength meter).
//           Continue gate: zxcvbn score ≥ 3.
//   Step 2: confirm the passphrase.
//   Step 3: explicit "no recovery" acknowledgement checkbox.
//   Step 4: create the primary OPFS vault, seal an initial app-config
//           to `/system/config.enc`, seed session stores, and land
//           in the browser.
//
// On error anywhere in step 4, the wizard surfaces the message and
// offers a "Start over" link back to step 1. Local passphrase state
// is cleared after a successful create; the Uint8Array we hand to
// the worker is zeroized in a `finally`.
//
// Master-password framing is UX vocabulary — under the hood this is
// just `openVault` + `saveAppConfig`. There is no `masterUnlock`
// worker method.

import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { zxcvbn } from "@zxcvbn-ts/core";
import { cn } from "../../lib/utils";
import { useVault } from "../../hooks/useVault";
import { useSession } from "../../stores/session";
import { useMasterSession } from "../../stores/masterSession";
import { toPassphraseBytes, zeroize } from "../../lib/passphraseBytes";
import type { AppConfig } from "../../worker/api";
import { PassphraseGenerator } from "../primitives/PassphraseGenerator";
import { PassphraseInput } from "../primitives/PassphraseInput";
import { PassphraseStrength } from "../primitives/PassphraseStrength";

// The primary OPFS vault lives at the origin root.
const OPFS_SUBDIR = "";
// Display name applied to the primary OPFS vault on first-time setup.
// Users can rename it later from settings.
const PRIMARY_DISPLAY_NAME = "primary";

/**
 * Render the step-2 confirmation feedback. Extracted from the JSX so
 * the previously-nested ternary becomes a single lookup.
 */
function confirmationMessage(confirmation: string, passphrase: string): string {
  if (confirmation.length === 0) return " ";
  if (confirmation === passphrase) return "✓ matches";
  return "× does not match";
}

export function MasterSetupWizard() {
  const { step: stepStr } = useParams();
  const step = stepStr ? Number(stepStr) : null;
  const navigate = useNavigate();
  const vault = useVault();
  const setSession = useSession((s) => s.setSession);
  const setMaster = useMasterSession((s) => s.setMaster);

  const [passphrase, setPassphrase] = useState("");
  const [useCustom, setUseCustom] = useState(false);
  const [confirmation, setConfirmation] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const createStartedRef = useRef(false);
  const firstInput = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    firstInput.current?.focus();
  }, [step]);

  // Memoize the zxcvbn score: it's only meaningfully displayed and
  // used for gating on step 1, but this component re-renders on every
  // keystroke of `confirmation` (step 2) and every toggle of
  // `acknowledged` (step 3) too. Recomputing zxcvbn — which is
  // non-trivial — on each unrelated re-render would be wasteful.
  // Keyed by `passphrase` so it only re-evaluates when the passphrase
  // itself changes. Must be declared BEFORE any early-return branches
  // below to satisfy React's Rules of Hooks.
  const zxcvbnScore = useMemo(
    () => (passphrase.length > 0 ? zxcvbn(passphrase).score : 0),
    [passphrase],
  );
  const strongEnough = zxcvbnScore >= 3;

  // `/setup` → `/setup/1`.
  if (step === null) {
    return <Navigate to="/setup/1" replace />;
  }

  // If step 2+ is reached without a valid passphrase chosen in step 1
  // (direct URL nav, refresh, etc.), bounce back to step 1 — the flow
  // is always top-down.
  if (step > 1 && passphrase.length === 0 && !createStartedRef.current) {
    return <Navigate to="/setup/1" replace />;
  }

  const stepsBar = (
    <div className="mb-5 flex gap-1.5">
      {[1, 2, 3, 4].map((i) => (
        <div
          key={i}
          className={cn(
            "h-[3px] flex-1 rounded-full",
            i < step && "bg-accent-soft",
            i === step && "bg-accent",
            i > step && "bg-border",
          )}
        />
      ))}
    </div>
  );

  if (step === 1) {
    return (
      <div className="mx-auto min-h-[70vh] max-w-md px-5 py-8 pb-[calc(env(safe-area-inset-bottom)+24px)] md:px-6 md:py-12">
        {stepsBar}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (strongEnough) {
              navigate("/setup/2");
            }
          }}
        >
          <h1 className="mb-2 text-2xl font-medium tracking-tight">
            Create your master password
          </h1>
          <p className="mb-4 text-sm leading-relaxed text-text-soft">
            This is the only password you'll need to remember. It unlocks your
            vault and all synced vaults on this device. If you lose it, your
            data is{" "}
            <strong className="text-danger">unrecoverable</strong>.
          </p>

          {!useCustom && (
            <>
              <PassphraseGenerator onChoose={setPassphrase} />
              {passphrase.length > 0 && (
                <div className="mt-2 text-[12px] text-text-soft">
                  Selected:{" "}
                  <span className="font-mono text-text">{passphrase}</span>
                </div>
              )}
              <div className="mt-3">
                <button
                  type="button"
                  onClick={() => {
                    setPassphrase("");
                    setUseCustom(true);
                  }}
                  className="text-[12px] text-accent-soft underline hover:text-text"
                >
                  Type my own passphrase
                </button>
              </div>
            </>
          )}

          {useCustom && (
            <>
              <label
                htmlFor="master-passphrase"
                className="mb-1 block text-[11px] uppercase tracking-[0.1em] text-text-dim"
              >
                Passphrase
              </label>
              <PassphraseInput
                ref={firstInput}
                id="master-passphrase"
                autoCompleteMode="new-password"
                aria-label="Passphrase"
                value={passphrase}
                onChange={(e) => setPassphrase(e.target.value)}
              />
              <PassphraseStrength passphrase={passphrase} />
              {!strongEnough && passphrase.length > 0 && (
                <div className="mt-1.5 text-[12px] text-danger">
                  Too weak — use 5+ random words or pick one from the
                  suggestions above.
                </div>
              )}
              <div className="mt-3">
                <button
                  type="button"
                  onClick={() => {
                    setPassphrase("");
                    setUseCustom(false);
                  }}
                  className="text-[12px] text-accent-soft underline hover:text-text"
                >
                  Use the generator instead
                </button>
              </div>
            </>
          )}

          <div className="mt-6 flex flex-col-reverse gap-2 md:flex-row md:justify-end">
            <button
              type="submit"
              disabled={!strongEnough}
              className="w-full rounded-md bg-accent px-4 py-3 text-sm font-medium text-white disabled:opacity-40 md:w-auto md:py-2"
            >
              Continue →
            </button>
          </div>
          <div className="mt-6 border-t border-border pt-3 text-center text-[12px] text-text-soft">
            Already have a backup file from another device?{" "}
            <Link
              to="/import"
              className="text-accent-soft underline hover:text-text"
            >
              Import from backup
            </Link>
          </div>
        </form>
      </div>
    );
  }

  if (step === 2) {
    return (
      <div className="mx-auto min-h-[70vh] max-w-md px-5 py-8 pb-[calc(env(safe-area-inset-bottom)+24px)] md:px-6 md:py-12">
        {stepsBar}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (confirmation === passphrase && confirmation.length > 0) {
              navigate("/setup/3");
            }
          }}
        >
          <h1 className="mb-2 text-2xl font-medium tracking-tight">
            Confirm your master password
          </h1>
          <p className="mb-4 text-sm leading-relaxed text-text-soft">
            Retype the password exactly. If you generated one, write it down
            somewhere safe before continuing — we cannot show it again after
            this step.
          </p>

          <label
            htmlFor="master-confirm"
            className="mb-1 block text-[11px] uppercase tracking-[0.1em] text-text-dim"
          >
            Retype passphrase
          </label>
          <PassphraseInput
            ref={firstInput}
            id="master-confirm"
            autoCompleteMode="new-password"
            aria-label="Retype passphrase"
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
          />
          <div
            className={cn(
              "mt-1.5 text-[11px]",
              confirmation.length === 0 && "text-text-dim",
              confirmation.length > 0 &&
                confirmation === passphrase &&
                "text-ok",
              confirmation.length > 0 &&
                confirmation !== passphrase &&
                "text-danger",
            )}
          >
            {confirmationMessage(confirmation, passphrase)}
          </div>

          <div className="mt-6 flex flex-col-reverse gap-2 md:flex-row md:justify-end">
            <button
              type="button"
              onClick={() => navigate("/setup/1")}
              className="w-full rounded-md border border-border px-4 py-3 text-sm text-text-soft md:w-auto md:py-2"
            >
              ← Back
            </button>
            <button
              type="submit"
              disabled={
                confirmation.length === 0 || confirmation !== passphrase
              }
              className="w-full rounded-md bg-accent px-4 py-3 text-sm font-medium text-white disabled:opacity-40 md:w-auto md:py-2"
            >
              Continue →
            </button>
          </div>
        </form>
      </div>
    );
  }

  if (step === 3) {
    return (
      <div className="mx-auto min-h-[70vh] max-w-md px-5 py-8 pb-[calc(env(safe-area-inset-bottom)+24px)] md:px-6 md:py-12">
        {stepsBar}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (acknowledged) {
              navigate("/setup/4");
            }
          }}
        >
          <h1 className="mb-2 text-2xl font-medium tracking-tight">
            No recovery password. Got it?
          </h1>
          <p className="mb-4 text-sm text-text-soft">
            If you forget this master password, there is no way to recover
            your data. Not by us, not by anyone. Make sure you remember it
            or store it somewhere safe (a password manager, a physical note
            in a safe place, etc.).
          </p>

          <div className="rounded-lg border border-danger/30 bg-danger/10 p-4">
            <label className="flex cursor-pointer items-center gap-3 text-[13px]">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                className="h-4 w-4 accent-danger"
                aria-label="I understand"
              />
              <span>I understand this password cannot be recovered.</span>
            </label>
          </div>

          <div className="mt-6 flex flex-col-reverse gap-2 md:flex-row md:justify-end">
            <button
              type="button"
              onClick={() => navigate("/setup/2")}
              className="w-full rounded-md border border-border px-4 py-3 text-sm text-text-soft md:w-auto md:py-2"
            >
              ← Back
            </button>
            <button
              type="submit"
              disabled={!acknowledged}
              className="w-full rounded-md bg-danger px-4 py-3 text-sm font-medium text-white disabled:opacity-40 md:w-auto md:py-2"
            >
              Create vault
            </button>
          </div>
        </form>
      </div>
    );
  }

  if (step === 4) {
    return (
      <Step4Create
        passphrase={passphrase}
        createError={createError}
        createStartedRef={createStartedRef}
        onStarted={() => {
          createStartedRef.current = true;
        }}
        onError={setCreateError}
        onSuccess={() => setPassphrase("")}
        vault={vault}
        setSession={setSession}
        setMaster={setMaster}
        navigate={navigate}
        onStartOver={() => {
          setPassphrase("");
          setConfirmation("");
          setAcknowledged(false);
          setUseCustom(false);
          setCreateError(null);
          createStartedRef.current = false;
        }}
        stepsBar={stepsBar}
      />
    );
  }

  // Out-of-range step → back to step 1.
  return <Navigate to="/setup/1" replace />;
}

interface VaultApi {
  createOpfsVault: (
    subdir: string,
    passphrase: Uint8Array,
    displayName: string,
    wipeExisting: boolean,
  ) => Promise<number>;
  saveAppConfig: (handle: number, cfg: AppConfig) => Promise<void>;
  lockVault: (handle: number) => Promise<void>;
}

interface SetupSuccess {
  vaultId: string;
  handle: number;
  appConfig: AppConfig;
  unlockedAt: number;
}

/**
 * Mint a stable opaque vault id used as the key inside
 * `appConfig.vaults` and in the URL path. Uses `crypto.randomUUID`
 * which is available in every modern browser and inside Web Workers
 * — no extra Wasm round-trip needed.
 */
function newVaultId(): string {
  return `v_${crypto.randomUUID()}`;
}

/**
 * Drive the four-step create pipeline. Extracted from Step4Create so
 * the wizard component keeps rendering + state and this function
 * holds the sequential async work (SonarCloud cognitive-complexity
 * floor).
 *
 * Rollback semantics: if `saveAppConfig` fails, the (successfully
 * created) vault is locked to drop the worker-side handle. The
 * on-disk vault.json + chunks remain — the user can retry from
 * `/setup/1`, which routes them through to /master against the
 * existing OPFS vault, except there's no app-config yet so the
 * unlock will fail at load_app_config and surface as a generic
 * error. The recovery is "Wipe app" from the unlock screen.
 */
async function runMasterSetup(
  vault: VaultApi,
  passphrase: Uint8Array,
): Promise<SetupSuccess> {
  const vaultId = newVaultId();

  const handle = await vault.createOpfsVault(
    OPFS_SUBDIR,
    passphrase,
    PRIMARY_DISPLAY_NAME,
    false,
  );

  try {
    const unlockedAt = Date.now();
    const appConfig: AppConfig = {
      format_version: 1,
      primary_vault_id: vaultId,
      vaults: [
        {
          id: vaultId,
          display_name: PRIMARY_DISPLAY_NAME,
          adapter: { type: "opfs", subdir: OPFS_SUBDIR },
          sync_state: "synced",
          pending_rotation: null,
          last_opened_at: Math.floor(unlockedAt / 1000),
        },
      ],
      preferences: {
        theme: "auto",
        auto_lock_minutes: 5,
        cache_prefs_by_vault_id: {},
      },
    };

    await vault.saveAppConfig(handle, appConfig);

    return { vaultId, handle, appConfig, unlockedAt };
  } catch (err) {
    // Rollback the worker-side handle. The on-disk vault remains
    // (best-effort: we don't try to wipe it here because that would
    // need a second adapter construction and the user can recover
    // via "Wipe app" on the next visit).
    await vault.lockVault(handle).catch(() => {
      /* best-effort */
    });
    throw err;
  }
}

interface Step4Props {
  readonly passphrase: string;
  readonly createError: string | null;
  readonly createStartedRef: React.MutableRefObject<boolean>;
  readonly onStarted: () => void;
  readonly onError: (msg: string | null) => void;
  readonly onSuccess: () => void;
  readonly vault: VaultApi;
  readonly setSession: (s: {
    handle: number;
    vaultId: string;
    displayName: string;
    unlockedAt: number;
  }) => void;
  readonly setMaster: (m: {
    masterHandle: number;
    appConfig: AppConfig;
    unlockedAt: number;
  }) => void;
  readonly navigate: (path: string) => void;
  readonly onStartOver: () => void;
  readonly stepsBar: React.ReactNode;
}

function Step4Create({
  passphrase,
  createError,
  createStartedRef,
  onStarted,
  onError,
  onSuccess,
  vault,
  setSession,
  setMaster,
  navigate,
  onStartOver,
  stepsBar,
}: Step4Props) {
  useEffect(() => {
    // Guard against double-runs from both React Strict Mode
    // double-invocation and any subsequent re-render. The ref
    // persists across strict-mode cleanup so only the first mount
    // launches the pipeline.
    if (createStartedRef.current) return;
    if (passphrase.length === 0) return;
    createStartedRef.current = true;
    onStarted();

    // Intentionally do NOT cancel on unmount: the create pipeline
    // must reach saveAppConfig atomically so the on-disk state is
    // consistent. If the component unmounts mid-flight (e.g. the
    // user navigates away), the worker still finishes the vault
    // write — the only downside is a stale session-store update
    // that will be overwritten on the next mount.

    async function run(): Promise<void> {
      const bytes = toPassphraseBytes(passphrase);
      try {
        const { vaultId, handle, appConfig, unlockedAt } =
          await runMasterSetup(vault, bytes);

        setMaster({ masterHandle: handle, appConfig, unlockedAt });
        setSession({
          handle,
          vaultId,
          displayName: PRIMARY_DISPLAY_NAME,
          unlockedAt,
        });

        onSuccess();
        navigate(`/v/${vaultId}/~/`);
      } catch (err) {
        onError(String(err instanceof Error ? err.message : err));
      } finally {
        zeroize(bytes);
      }
    }

    run().catch((err) => {
      // runMasterSetup and the orchestration above already catch
      // everything and surface via `onError`; this tail-catch is
      // belt-and-suspenders so a stray rejection never slips past.
      onError(String(err instanceof Error ? err.message : err));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="mx-auto min-h-[70vh] max-w-md px-5 py-8 pb-[calc(env(safe-area-inset-bottom)+24px)] md:px-6 md:py-12">
      {stepsBar}
      {createError === null ? (
        <div className="flex flex-col items-center justify-center py-10 text-center">
          <div className="mb-3 h-5 w-5 animate-spin rounded-full border-2 border-accent border-t-transparent" />
          <div className="text-sm text-text-soft">Creating your vault…</div>
        </div>
      ) : (
        <div className="rounded-lg border border-danger/40 bg-danger/10 p-4">
          <div className="mb-2 text-sm font-semibold text-danger">
            Vault creation failed
          </div>
          <p className="mb-3 text-[13px] leading-relaxed text-text">
            {createError}
          </p>
          <Link
            to="/setup/1"
            onClick={onStartOver}
            className="text-[13px] text-accent-soft underline hover:text-text"
          >
            ← Start over
          </Link>
        </div>
      )}
    </div>
  );
}
